import React, { useEffect, useState } from 'react';
import DropdownSelect from '../Generic/DropdownSelect';
import { Col, Form, Spinner } from 'react-bootstrap';
import NumberInput from '../Generic/NumberInput';
import Button from 'react-bootstrap/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import DiscountedPriceComponent from '../Generic/DiscountedPriceComponent';
import OrderProductLabel from '../Order/OrderProductLabel';

/**
 * Component: QuoteItemComponent
 *
 * Description: Component used to render a single item in a quote form.
 *
 * Props:
 *   - productList: An array of objects representing the available products.
 *   - disabledOptions: An array of values representing the disabled product options.
 *   - editMode: A boolean indicating whether the quote is in edit mode.
 *   - onItemChange: A function that handles changes to the item fields.
 *   - index: The index of the item in the form.
 *   - item: The current value of the item (product id).
 *   - productRecord: Full product object when editing an existing quote line.
 *   - handleItemDelete: A function that handles the deletion of an item.
 *   - showRemoveButton: A boolean indicating whether to show the remove button.
 *   - isUniformCurrency: A boolean indicating whether the currency is uniform.
 *   - uniformCurrencyValue: The uniform currency value.
 *   - formik: The formik object used for form management.
 */
const QuoteItemComponent = ({
  productList,
  disabledOptions,
  editMode,
  onItemChange,
  index,
  item,
  productRecord = null,
  handleItemDelete,
  showRemoveButton,
  isUniformCurrency,
  uniformCurrencyValue,
  formik,
}) => {
  const [typingTimeout, setTypingTimeout] = useState(null);
  const foundProduct = item ? productList.find(p => p.value === item) : null;
  const [product, setProduct] = useState(foundProduct);
  const [productSelectListTouched, setProductSelectListTouched] = useState(
    !!product
  );
  const [showDiscountField, setShowDiscountField] = useState(false);

  useEffect(() => {
    if (formik.values.items?.[index]?.discount) setShowDiscountField(true);
  }, [formik, index]);

  useEffect(() => {
    if (!editMode) setProduct(null);
  }, [productList, editMode]);

  useEffect(() => {
    if (item) {
      const next = productList.find(p => p.value === item) || null;
      setProduct(next);
    }
  }, [item, productList]);

  const toggleShowDiscountField = () => {
    setShowDiscountField(prevState => !prevState);
  };

  const handleBlur = () => setProductSelectListTouched(true);

  const handleDelayedChange = (name, value) => {
    if (typingTimeout) clearTimeout(typingTimeout);

    const newTimeout = setTimeout(() => {
      onItemChange(index, name, value);
    }, 300);

    setTypingTimeout(newTimeout);
  };

  const handleProductChange = newValue => {
    setProduct(newValue);
    onItemChange(index, 'product', newValue.value);
  };

  const quantityFieldName = `items[${index}].quantity`;
  const priceFieldName = `items[${index}].price`;
  const discountFieldName = `items[${index}].discount`;
  const currencyFieldName = `items[${index}].currency`;

  useEffect(() => {
    if (isUniformCurrency && uniformCurrencyValue) {
      const currentCurrency = formik.values?.items?.[index]?.currency;
      if (currentCurrency !== uniformCurrencyValue) {
        formik.setFieldValue(currencyFieldName, uniformCurrencyValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUniformCurrency, uniformCurrencyValue, currencyFieldName, index]);

  const productForLabel =
    productRecord ||
    (product
      ? { id: product.value, name: product.label }
      : item
        ? { id: item, name: `Product ${item}` }
        : null);

  if (!productList) {
    return (
      <Spinner
        size="lg"
        as="span"
        animation="border"
        role="status"
        aria-hidden="true"
      />
    );
  }

  return (
    <div className="order-item-panel order-glass-panel">
      <h3 className="order-item-panel__title">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fs-4">🧪</span>
          {productForLabel ? (
            <OrderProductLabel
              product={productForLabel}
              productId={productForLabel.id}
              triggerClassName="order-product-modal-trigger"
              prefetchOnHover={false}
            />
          ) : (
            <span>Item {index + 1}</span>
          )}
        </div>
      </h3>

      <div className="order-lot-panel">
        <h5 className="order-lot-panel__heading">Product</h5>
        <div className="d-flex flex-row">
          <DropdownSelect
            optionsList={productList}
            label="Select a product"
            selectedValue={product}
            setSelectedValue={handleProductChange}
            disabledOptions={disabledOptions}
            isInvalid={!product && productSelectListTouched}
            touched={productSelectListTouched}
            error="This field is required"
            onBlur={handleBlur}
          />
          <RequiredAsteriskComponent />
        </div>
      </div>

      <div className="order-lot-panel">
        <h5 className="order-lot-panel__heading">Pricing & currency</h5>

        <Form.Group
          controlId={`itemQuantity${index}`}
          className="field-margin mt-2"
        >
          <Form.Label>
            Item Quantity <RequiredAsteriskComponent />
          </Form.Label>
          <NumberInput
            name={quantityFieldName}
            integerOnly
            value={formik.values?.items[index]?.quantity || ''}
            onChange={event => {
              formik.handleChange(event);
              handleDelayedChange('quantity', event.target.value);
            }}
            onBlur={formik.handleBlur}
            isValid={
              !formik.errors.items?.[index]?.quantity &&
              formik.values.items?.[index]?.quantity
            }
            isInvalid={
              (formik.touched.items?.[index]?.quantity &&
                !formik.values.items?.[index]?.quantity) ||
              (formik.errors.items?.[index]?.quantity &&
                formik.errors.items?.[index]?.quantity !==
                  'Quantity is required' &&
                formik.values.items?.[index]?.quantity)
            }
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.items?.[index]?.quantity}
          </Form.Control.Feedback>
          <Form.Control.Feedback type="valid">Looks good!</Form.Control.Feedback>
        </Form.Group>

        <Form.Group controlId={`itemPrice${index}`} className="field-margin">
          <Form.Label>
            Item Price <RequiredAsteriskComponent />
          </Form.Label>
          <NumberInput
            name={priceFieldName}
            value={formik.values?.items[index]?.price || ''}
            onChange={event => {
              formik.handleChange(event);
              handleDelayedChange('price', event.target.value);
            }}
            onBlur={formik.handleBlur}
            isValid={
              !formik.errors.items?.[index]?.price &&
              formik.values.items?.[index]?.price
            }
            isInvalid={
              (formik.touched.items?.[index]?.price &&
                !formik.values.items?.[index]?.price) ||
              (formik.errors.items?.[index]?.price &&
                formik.errors.items?.[index]?.price !== 'Price is required' &&
                formik.values.items?.[index]?.price)
            }
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.items?.[index]?.price}
          </Form.Control.Feedback>
          <Form.Control.Feedback type="valid">Looks good!</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="d-flex flex-row align-items-center">
          <Form.Label className="me-3 mb-0">Discount given?</Form.Label>
          <Form.Switch
            checked={showDiscountField}
            onChange={toggleShowDiscountField}
          />
        </Form.Group>

        {showDiscountField ? (
          <Form.Group controlId="formProductDiscount" className="field-margin">
            <Form.Label>Discount</Form.Label>
            <NumberInput
              name={discountFieldName}
              value={formik.values?.items?.[index]?.discount || ''}
              onChange={event => {
                formik.handleChange(event);
                handleDelayedChange('discount', event.target.value);
              }}
              onBlur={formik.handleBlur}
              isValid={
                !formik.errors.items?.[index]?.discount &&
                formik.values.items?.[index]?.discount
              }
              isInvalid={
                formik.errors.items?.[index]?.discount &&
                formik.values.items?.[index]?.discount
              }
            />
            <Form.Control.Feedback type="valid">
              Looks good!
            </Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {formik.errors?.items?.[index]?.discount}
            </Form.Control.Feedback>
            {formik.values.items?.[index]?.price &&
              formik.values.items?.[index]?.discount &&
              !formik.errors.items?.[index]?.discount && (
                <Form.Text style={{ fontSize: 20 }}>
                  <DiscountedPriceComponent
                    product={{
                      price: formik.values.items?.[index]?.price,
                      discount: formik.values.items?.[index]?.discount,
                      currency: formik.values.items?.[index]?.currency,
                    }}
                  />
                </Form.Text>
              )}
          </Form.Group>
        ) : null}

        <Form.Group
          as={Col}
          md="8"
          controlId="formProductCurrency"
          className="field-margin mt-3"
        >
          <Form.Label>
            Currency <RequiredAsteriskComponent />
          </Form.Label>
          <Form.Select
            name={currencyFieldName}
            value={
              (isUniformCurrency && uniformCurrencyValue) ||
              formik.values?.items?.[index]?.currency ||
              ''
            }
            onChange={event => {
              formik.handleChange(event);
              const { value } = event.target;
              handleDelayedChange('currency', value);
            }}
            onBlur={formik.handleBlur}
            isValid={formik.values?.items?.[index]?.currency}
            isInvalid={
              formik.touched?.items?.[index]?.currency &&
              !formik.values?.items?.[index]?.currency
            }
          >
            <option value="" disabled>
              -- Select currency --
            </option>
            <option value="NIS">NIS</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </Form.Select>
          <Form.Control.Feedback type="valid">Looks good!</Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {formik.errors?.items?.[index]?.currency}
          </Form.Control.Feedback>
        </Form.Group>
      </div>

      {!showRemoveButton && (
        <Button
          variant="outline-danger"
          className="mt-2"
          onClick={e => {
            handleItemDelete(e, index);
          }}
        >
          <DeleteIcon />
        </Button>
      )}
    </div>
  );
};
export default QuoteItemComponent;
